import { useCallback, useEffect, useRef, useState } from "react";

const runAfterPaint = (callback) => {
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(callback);
    return;
  }

  window.setTimeout(callback, 0);
};

export const useScrollState = ({
  isMobile,
  focusSource,
  isMostVisitedMode,
  isSavedMode,
}) => {
  const mainRef = useRef(null);
  const scrollYBeforeModalRef = useRef(0);
  const scrollYBeforeSavedRef = useRef(0);

  const [isAtTop, setIsAtTop] = useState(true);
  const [mainScrollParent, setMainScrollParent] = useState(null);
  const [contentScrollParent, setContentScrollParent] = useState(null);
  const [focusScrollToTopSignal, setFocusScrollToTopSignal] = useState(0);
  const [isFocusPageAtTop, setIsFocusPageAtTop] = useState(true);
  const [mostVisitedScrollToTopSignal, setMostVisitedScrollToTopSignal] =
    useState(0);
  const [isMostVisitedPageAtTop, setIsMostVisitedPageAtTop] = useState(true);
  const [savedScrollToTopSignal, setSavedScrollToTopSignal] = useState(0);
  const [isSavedPageAtTop, setIsSavedPageAtTop] = useState(true);

  const setMainElement = useCallback((node) => {
    mainRef.current = node;
    setMainScrollParent(node);
  }, []);

  const setContentWrapperElement = useCallback((node) => {
    setContentScrollParent(node);
  }, []);

  useEffect(() => {
    const currentMain = isMobile ? contentScrollParent : mainScrollParent;

    const checkScroll = () => {
      if (currentMain && currentMain.scrollHeight > currentMain.clientHeight) {
        setIsAtTop(currentMain.scrollTop === 0);
      } else {
        setIsAtTop(window.scrollY === 0);
      }
    };

    if (currentMain) {
      currentMain.addEventListener("scroll", checkScroll, { passive: true });
    }

    window.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();

    return () => {
      if (currentMain) {
        currentMain.removeEventListener("scroll", checkScroll);
      }

      window.removeEventListener("scroll", checkScroll);
    };
  }, [contentScrollParent, isMobile, mainScrollParent]);

  const rememberMostVisitedScroll = useCallback(() => {
    const scrollTarget = isMobile
      ? contentScrollParent
      : mainScrollParent;

    scrollYBeforeModalRef.current = scrollTarget
      ? scrollTarget.scrollTop || 0
      : window.scrollY;
  }, [contentScrollParent, isMobile, mainScrollParent]);

  const restoreMostVisitedScroll = useCallback(() => {
    runAfterPaint(() => {
      const scrollTarget = isMobile
        ? contentScrollParent
        : mainScrollParent;

      if (scrollTarget) {
        scrollTarget.scrollTo({
          top: scrollYBeforeModalRef.current,
          behavior: "auto",
        });
        return;
      }

      window.scrollTo(0, scrollYBeforeModalRef.current);
    });
  }, [contentScrollParent, isMobile, mainScrollParent]);

  const rememberSavedScroll = useCallback(() => {
    scrollYBeforeSavedRef.current = isMobile
      ? contentScrollParent?.scrollTop || 0
      : window.scrollY;
  }, [contentScrollParent, isMobile]);

  const restoreSavedScroll = useCallback(() => {
    runAfterPaint(() => {
      if (isMobile && contentScrollParent) {
        contentScrollParent.scrollTo({
          top: scrollYBeforeSavedRef.current,
          behavior: "auto",
        });
      } else {
        window.scrollTo(0, scrollYBeforeSavedRef.current);
      }
    });
  }, [contentScrollParent, isMobile]);

  const scrollAppToTop = useCallback(() => {
    runAfterPaint(() => {
      const scrollTarget = isMobile ? contentScrollParent : mainRef.current;

      if (scrollTarget) {
        scrollTarget.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }

      window.scrollTo(0, 0);
    });
  }, [contentScrollParent, isMobile]);

  const scrollToTop = useCallback(() => {
    if (isMobile && focusSource !== null && !isMostVisitedMode && !isSavedMode) {
      setFocusScrollToTopSignal((prev) => prev + 1);
      return;
    }

    /*
    |-----------------------------------------------------------------------
    | Most Visited scroll owner
    |-----------------------------------------------------------------------
    |
    | Mobile renders a separate vertically-scrollable page for each period,
    | so the pager must receive a signal and scroll its active page.
    |
    | Desktop renders Most Visited inside the normal main application
    | scroller. Do not return early there; let the shared desktop scroll
    | target below handle the arrow button.
    |
    */
    if (isMostVisitedMode && isMobile) {
      setMostVisitedScrollToTopSignal((prev) => prev + 1);
      setIsAtTop(true);
      setIsMostVisitedPageAtTop(true);
      return;
    }

    if (isSavedMode && isMobile) {
      setSavedScrollToTopSignal((prev) => prev + 1);
      setIsAtTop(true);
      setIsSavedPageAtTop(true);
      return;
    }

    const scrollTarget = isMobile ? contentScrollParent : mainRef.current;

    if (scrollTarget && scrollTarget.scrollHeight > scrollTarget.clientHeight) {
      scrollTarget.scrollTo({
        top: 0,
        behavior: "auto",
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }
  }, [contentScrollParent, focusSource, isMobile, isMostVisitedMode, isSavedMode]);

  const scrollToNewsItem = useCallback((link) => {
    const element = document.getElementById(`news-${encodeURIComponent(link)}`);

    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    element.classList.add("news-card--highlight");

    window.setTimeout(() => {
      element.classList.remove("news-card--highlight");
    }, 2000);
  }, []);

  return {
    mainRef,
    mainScrollParent,
    contentScrollParent,
    setMainElement,
    setContentWrapperElement,
    isAtTop,
    scrollToTop,
    scrollToNewsItem,
    scrollAppToTop,
    rememberMostVisitedScroll,
    restoreMostVisitedScroll,
    rememberSavedScroll,
    restoreSavedScroll,
    focusScrollToTopSignal,
    isFocusPageAtTop,
    setIsFocusPageAtTop,
    mostVisitedScrollToTopSignal,
    isMostVisitedPageAtTop,
    setIsMostVisitedPageAtTop,
    savedScrollToTopSignal,
    isSavedPageAtTop,
    setIsSavedPageAtTop,
    setSavedScrollToTopSignal,
  };
};
